import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import Mark from '../../../../ui/mark';
import {
  StateSelectSC,
  OptionsWrapper,
  MenuWrapper,
  AllWrapper,
  InputWrapper,
} from './state-select.styled';
import { ReactComponent as ChevronIcon } from '../../../../../assets/img/svg/chevron-down.svg';
import { SmallText } from '../../../../typography';
import StateCheckbox from '../state-checkbox/state-checkbox';
import OptionItem from '../option-item/option-item';
import ALL_STATUSES from '../../../../../types/investments-types';

/**
 * Get color of mark for status of investment
 * @param {*} type investment type
 */
function getMarkColor(type) {
  switch (type) {
    case 'paying':
      return 'GREEN';
    case 'medium-delay':
      return 'GRAY';
    case 'high-delay':
      return 'RED';
    case 'paid':
      return 'DARK_GREEN';
    case 'sold':
      return 'OUTLINE_GRAY';
    case 'for-transfer':
      return 'ORANGE';
    default:
      return '';
  }
}

/**
 * Component for state selection
 * @param {any} param0 props
 */
function StateSelect({ value = [], onChange }) {
  const { formatMessage } = useIntl();
  const [menuOpen, setMenuOpen] = useState(false);
  const nodeRef = useRef(null);

  /**
   * Function to calculate collision of mouse with dropdown
   * @param e event
   */
  const handleOutsideClick = useCallback(
    (e) => {
      const domNode = ReactDOM.findDOMNode(nodeRef.current);
      if (domNode && !domNode.contains(e.target)) {
        setMenuOpen(false);
      }
    },
    [nodeRef]
  );

  /**
   * Register listener for click outside dropdown
   */
  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.addEventListener('mousedown', handleOutsideClick);
    };
  }, [handleOutsideClick]);

  const selectedStatus = useMemo(() => {
    if (!value || value.length === 0) {
      return 'none';
    } else if (value && value.length < 5) {
      return 'partial';
    } else {
      return 'all';
    }
  }, [value]);

  /**
   * Function to render options on closed select
   */
  function getSelected() {
    if (value && value.length === 5) {
      return (
        <SmallText style={{ marginLeft: 8 }} className="bold">
          {formatMessage({ id: 'table_filter_state_all' })}
        </SmallText>
      );
    } else if (value && value.length) {
      return value?.map((item) => (
        <Mark colorStyle={getMarkColor(item)} size="small" style={{ marginRight: 8 }} key={item}>
          {formatMessage({ id: `table_filter_state_${item}` })[0]}...
        </Mark>
      ));
    } else {
      return (
        <SmallText style={{ marginLeft: 8 }} className="bold">
          -
        </SmallText>
      );
    }
  }

  /**
   * Handle toggle for all select
   */
  function onAllClick() {
    if (onChange) {
      if (selectedStatus === 'none' || selectedStatus === 'partial') {
        onChange(ALL_STATUSES);
      } else {
        onChange([]);
      }
    }
  }

  /**
   * handle click on select item
   * @param {*} key key of item
   * @param {*} value value if selected
   */
  function handleOptionClick(key, selected) {
    if (onChange) {
      if (selected) {
        return onChange([...value, key]);
      } else {
        return onChange(value.filter((v) => v !== key));
      }
    }
  }

  return (
    <InputWrapper ref={nodeRef}>
      <StateSelectSC onClick={() => setMenuOpen((o) => !o)} open={menuOpen}>
        <OptionsWrapper>{getSelected()}</OptionsWrapper>
        <ChevronIcon />
      </StateSelectSC>
      {menuOpen && (
        <MenuWrapper>
          <AllWrapper>
            <SmallText className="bold">
              {formatMessage({ id: 'table_filter_state_all' })}
            </SmallText>
            <StateCheckbox state={selectedStatus} onClick={onAllClick} />
          </AllWrapper>
          {ALL_STATUSES.map((s) => (
            <OptionItem checked={value.includes(s)} key={s} onClick={handleOptionClick} value={s}>
              <Mark colorStyle={getMarkColor(s)} size="small" style={{ marginRight: 8 }}>
                {formatMessage({ id: `table_filter_state_${s}` })}
              </Mark>
            </OptionItem>
          ))}
        </MenuWrapper>
      )}
    </InputWrapper>
  );
}

export default StateSelect;
